import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import apiClient from "@/lib/apiClient";
import { Helmet } from 'react-helmet-async';
import { Mail, CheckCircle, Send, ArrowLeft } from "lucide-react";

interface Site {
  id: number;
  name: string;
  subdomain: string;
}

interface ResetPasswordPageProps {
  siteId: number;
  siteData?: Site;
}

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ siteId, siteData }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setMessage("");

    try {
      console.log("パスワード再設定のリクエストを送信します");
      const response = await apiClient.post("/customer/resets_password", {
        email,
        site_subdomain: siteData?.subdomain
      });
      setMessage(response.data.message);
    } catch (err: any) {
      setError(err.response?.data?.error || "パスワードリセットリクエストに失敗しました");
    }
  };

  return (
    <>
      <Helmet>
        <title>パスワード再設定</title>
        <meta property="og:title" content="パスワード再設定" />
      </Helmet>

      <div className="min-h-screen bg-theme-bg flex flex-col">
        <div className="flex-1 flex items-center justify-center py-12 px-4 pt-24">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-theme-primary border-theme rounded-theme-lg flex items-center justify-center mx-auto mb-4 shadow-theme">
                <Mail className="w-8 h-8 text-theme-button" />
              </div>
              <h2 className="text-3xl [font-weight:var(--theme-font-weight)] text-theme-bg-text">
                パスワード再設定
              </h2>
              <p className="mt-2 [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70">
                登録済みのメールアドレスに再設定用のリンクをお送りします
              </p>
            </div>

            {message ? (
              <div className="bg-white border-theme rounded-theme-lg shadow-theme p-6 sm:p-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-400 border-theme rounded-theme-lg flex items-center justify-center mx-auto mb-4 shadow-theme-sm">
                    <CheckCircle className="w-8 h-8 text-black" />
                  </div>
                  <h3 className="text-lg [font-weight:var(--theme-font-weight)] text-black mb-4">送信完了</h3>
                  <div className="p-4 bg-green-100 border-2 border-green-400 rounded-theme mb-4">
                    <p className="[font-weight:var(--theme-font-weight-button)] text-green-700">{message}</p>
                  </div>
                  <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600 mb-6">
                    メールが届かない場合は、迷惑メールフォルダをご確認ください。
                  </p>
                  <a
                    href="/login"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-black shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    ログイン画面に戻る
                  </a>
                </div>
              </div>
            ) : (
              <div className="bg-white border-theme rounded-theme-lg shadow-theme p-6 sm:p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="p-4 bg-red-100 border-2 border-red-400 rounded-theme">
                      <p className="[font-weight:var(--theme-font-weight-button)] text-red-700">{error}</p>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="email" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                      <Mail className="h-4 w-4" />
                      メールアドレス
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="test@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                    />
                    <p className="mt-2 text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500">
                      ご登録いただいたメールアドレスを入力してください
                    </p>
                  </div>

                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-theme-primary border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-theme-button shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] active:shadow-none active:translate-x-[4px] active:translate-y-[4px] transition-all"
                  >
                    <Send className="w-4 h-4" />
                    再設定用リンクを送信
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <a
                    href="/login"
                    className="inline-flex items-center gap-2 [font-weight:var(--theme-font-weight-button)] text-theme-primary hover:opacity-80"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    ログイン画面に戻る
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ResetPasswordPage;
